import React from 'react';
import { Box, Divider, Typography, useTheme } from '@mui/material';
import { SubscriptionUIModel } from '../types/subscription';
import {
  SummaryLabel,
  SummaryRow,
  SummaryTitle,
  SummaryValue,
  TotalContainer,
  TotalLabel,
  TotalValue,
} from './style';
import { useSearchParams } from 'react-router-dom';

interface MembershipSummaryProps {
  subscription: SubscriptionUIModel;
  discount: number;
  promoDiscount?: number;
  promoCode?: string;
  promoType?: string;
  isTrialEligible?: boolean;
}

const MembershipSummary: React.FC<MembershipSummaryProps> = ({
  subscription,
  discount = 0,
  promoDiscount = 0,
  promoCode = '',
  promoType = '',
  isTrialEligible = false,
}) => {
  const theme = useTheme();
  const referralDiscountAmount = Math.max((subscription.price * discount) / 100, 0);
  const priceAfterReferral = subscription.price - referralDiscountAmount;
  // Cap promo discount at remaining price after referral discount
  const cappedPromoDiscount = Math.min(promoDiscount, priceAfterReferral);
  const total = subscription.price - referralDiscountAmount - cappedPromoDiscount;
  const [searchParams] = useSearchParams();
  const isAddOn = Boolean(searchParams.get('addOn'));

  // If trial eligible, show trial summary
  if (isTrialEligible && !isAddOn) {
    return (
      <Box>
        <SummaryTitle variant='h6'>Membership Summary</SummaryTitle>

        <SummaryRow>
          <SummaryLabel>Free Trial</SummaryLabel>
          <SummaryValue>$0.00</SummaryValue>
        </SummaryRow>

        <SummaryRow>
          <SummaryLabel>Included MindRight Moments</SummaryLabel>
          <SummaryValue>15 — $0.00</SummaryValue>
        </SummaryRow>

        <Divider
          sx={{
            my: 1.5,
            backgroundColor: theme.palette.divider,
          }}
        />

        <TotalContainer>
          <TotalLabel>Total Due Today</TotalLabel>
          <TotalValue>$0.00</TotalValue>
        </TotalContainer>

        {/* Billing After Trial Section */}
        <Box sx={{ mt: 4 }}>
          <SummaryTitle variant='h6' sx={{ mb: 2 }}>
            Billing After Trial
          </SummaryTitle>

          <SummaryRow>
            <SummaryLabel>Plan: {subscription.title}</SummaryLabel>
            <SummaryValue>${subscription.price.toFixed(2)}/month</SummaryValue>
          </SummaryRow>

          <SummaryRow>
            <SummaryLabel>Included MindRight Moments</SummaryLabel>
            <SummaryValue>{subscription.moments}</SummaryValue>
          </SummaryRow>

          {referralDiscountAmount > 0 && (
            <SummaryRow>
              <SummaryLabel>Referral Discount ({discount}%)</SummaryLabel>
              <SummaryValue>- ${referralDiscountAmount.toFixed(2)}</SummaryValue>
            </SummaryRow>
          )}

          {promoDiscount > 0 && (
            <SummaryRow>
              <SummaryLabel>
                {promoType === 'gift_card' ? 'Gift Card' : 'Promo Code'}
                {promoCode ? ` (${promoCode})` : ''}
              </SummaryLabel>
              <SummaryValue>- ${cappedPromoDiscount.toFixed(2)}</SummaryValue>
            </SummaryRow>
          )}

          <Divider
            sx={{
              my: 1.5,
              backgroundColor: theme.palette.divider,
            }}
          />

          <TotalContainer>
            <TotalLabel>Total After Trial</TotalLabel>
            <TotalValue>${Math.max(0, total).toFixed(2)}/month</TotalValue>
          </TotalContainer>
        </Box>

        {/* Trial Disclosure */}
        <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography
            variant='body2'
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.875rem',
              lineHeight: 1.5,
            }}
          >
            After your free trial, your membership will automatically renew using the plan you
            selected unless you cancel before the trial ends.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Normal summary (non-trial)
  return (
    <Box>
      <SummaryTitle variant='h6'>Membership Summary</SummaryTitle>

      <SummaryRow>
        <SummaryLabel>{subscription.title}</SummaryLabel>
        <SummaryValue>${subscription.price.toFixed(2)}</SummaryValue>
      </SummaryRow>
      {isAddOn ? (
        <SummaryRow></SummaryRow>
      ) : (
        <SummaryRow>
          <SummaryLabel>Monthly Subscription</SummaryLabel>
          <SummaryValue>${subscription.price.toFixed(2)}/month</SummaryValue>
        </SummaryRow>
      )}

      <SummaryRow>
        <SummaryLabel>Included MindRight Moments</SummaryLabel>
        <SummaryValue>{subscription.moments}</SummaryValue>
      </SummaryRow>
      {referralDiscountAmount > 0 && (
        <SummaryRow>
          <SummaryLabel>Referral Discount ({discount}%)</SummaryLabel>
          <SummaryValue>- ${referralDiscountAmount.toFixed(2)}</SummaryValue>
        </SummaryRow>
      )}

      {promoDiscount > 0 && (
        <SummaryRow>
          <SummaryLabel>
            {promoType === 'gift_card' ? 'Gift Card' : 'Promo Code'}
            {promoCode ? ` (${promoCode})` : ''}
          </SummaryLabel>
          <SummaryValue>- ${cappedPromoDiscount.toFixed(2)}</SummaryValue>
        </SummaryRow>
      )}
      <Divider
        sx={{
          my: 1.5,
          backgroundColor: theme.palette.divider,
        }}
      />

      <TotalContainer>
        <TotalLabel>Total</TotalLabel>
        <TotalValue>${Math.max(0, total).toFixed(2)}</TotalValue>
      </TotalContainer>
    </Box>
  );
};

export default MembershipSummary;
